import { LOCAL_STORAGE_LOGIN_FROM } from "contexts";
import sequenceInstance from "contexts/SessionSequenceProvider";
import { useAppSelector } from "hooks";
import { IPlaygamiAnalyticProps } from "interfaces/analytics/playgami";
import { useCallback } from "react";
import { selectWebStoreState } from "redux/slices";
import { analyticsTrackerV2 } from "contexts/ScopelyIdentityProvider/configv2";
import { ILoginResponse } from "../../../contexts/WebStoreProvider/types";
import { AbstractHttpService } from "../../../infrastructure/HttpService";
import HttpRequestBuilder from "../../../infrastructure/HttpService/HttpRequest/httpRequestBuilder";
import HttpResponse from "../../../infrastructure/HttpService/HttpResponse";
import { ContentType } from "../../../infrastructure/HttpService/headerConstants";
import { RequestAuthOption } from "../../../infrastructure/HttpService/types";
import { getDebugLogger, IDebugLogger } from "../../../utils/debugLogger";
import { LoginGoogleAnalyticsHelper } from "../LoginGoogleAnalyticsHelper";
import { AbstractLoginRequest } from "../LoginRequest";
import { ILoginCallbacks } from "./types";
import { getPlatform } from "utils/platform";
import { getIsStandalonePWA } from "components/PWA/helpers/getIsStandalonePWA";

const LOGIN_ENDPOINT = "/rest/user/login";
const debug: IDebugLogger = getDebugLogger({ isEnabled: true, color: "Cyan" });

type UseLoginServiceDeps = {
	httpService: AbstractHttpService;
	loginGoogleAnalytics: LoginGoogleAnalyticsHelper;
	reportPlaygamiEvent: (
		eventName: "tp.web_frontend",
		eventValues: IPlaygamiAnalyticProps
	) => void;
	loginCallbacks: ILoginCallbacks;
};

export const useLoginService = ({
	httpService,
	loginGoogleAnalytics,
	reportPlaygamiEvent,
	loginCallbacks
}: UseLoginServiceDeps) => {
	const { sessionId } = useAppSelector(selectWebStoreState);

	const login = useCallback(
		async (
			loginRequest: AbstractLoginRequest
		): Promise<ILoginResponse | undefined> => {
			loginGoogleAnalytics.reportLoginStart(loginRequest.loginType);
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "login_start",
				event_type: "login",
				action: "login_start",
				login_type: loginRequest.loginType
			});
			const syntheticId = localStorage.getItem(
				"store.solitairetripeaks.com:syntheticId"
			);
			const authTokenTemp = localStorage.getItem(
				"store.solitairetripeaks.com:authTokenTemp"
			);
			const deviceToken = analyticsTrackerV2.getDeviceToken();
			const platform = getPlatform();
			const request = new HttpRequestBuilder(LOGIN_ENDPOINT, "POST")
				.withHeaders({
					"Content-Type": ContentType.PlainText,
					"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId
						? JSON.parse(syntheticId)
						: "",
					"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId || "",
					"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA()
						? "pwa"
						: "web",
					"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
						.incrementAndGetSequence()
						.toString(),
					"X-SCOPELY-WEBSTORE-PLATFORM": platform,
					"X-TITAN-DEVICE-TOKEN": deviceToken || "",
					"X-TITAN-METADATA": JSON.stringify({
						app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
						environment_id:
							process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
					}),
					...(authTokenTemp &&
						localStorage.getItem(LOCAL_STORAGE_LOGIN_FROM) ===
							"signup_incentive" && {
							Authorization: `Bearer ${JSON.parse(authTokenTemp).value}`
						})
				})
				.withParams({
					loginType: loginRequest.loginType,
					loginCredentials: loginRequest.getCredentialsAsString()
				})
				.build();

			return httpService
				.makeRequest(request, RequestAuthOption.NoAuth)
				.then((response: HttpResponse) => {
					const { success, errorCode, errorMessage } = response.data;
					if (success) {
						loginGoogleAnalytics.reportLoginSuccess(loginRequest.loginType);
						reportPlaygamiEvent("tp.web_frontend", {
							event_name: "login_success",
							event_type: "login",
							action: "login_success",
							login_type: loginRequest.loginType
						});
						loginCallbacks.onLoginSuccess(response.data);
					} else {
						debug.log({
							iconText: "LoginService",
							message: [
								"Login Call Returned Fail:",
								`errorCode: ${errorCode}`,
								`errorMessage: ${errorMessage}`
							]
						});
						loginGoogleAnalytics.reportLoginFailure(loginRequest.loginType);
						reportPlaygamiEvent("tp.web_frontend", {
							event_name: "login_failure",
							event_type: "login",
							action: "login_failure",
							login_type: loginRequest.loginType,
							error_type: `errorCode: ${errorCode}, errorMessage: ${errorMessage}`
						});
						loginCallbacks.onLoginFail(response.data);
					}
					return response.data;
				})
				.catch((err) => {
					debug.log({
						iconText: "LoginService",
						message: ["Error Calling Login Service:", err]
					});
					loginCallbacks.onLoginError(err);
				});
		},
		[httpService, loginGoogleAnalytics, reportPlaygamiEvent, loginCallbacks]
	);

	return { login };
};

export default useLoginService;
